import type { IDatadogLogger } from "./DatadogLoggerTypes";

/**
 * Shared Datadog config helpers for the profile and RNVJ viewjob **web**
 * bundles. Extracted from the two previously-identical copies in
 * `web/profile/src/datadog/datadogConfig.ts` and
 * `web/viewjob/src/datadog/datadogConfig.ts` (RN-5721).
 *
 * Only the *stateless* pieces live here — the config shape + guard, URL/PII
 * scrubbing, `Error` reconstruction, and the logs-only logger methods. Init
 * flow, RUM wiring, and host-context sync stay per-surface because they diverge
 * by design: profile self-initializes at module load and ships
 * `@datadog/browser-rum`; viewjob is dependency-injected (for the
 * strict/non-strict bundle split) and stubs RUM.
 *
 * This module is intentionally free of any `@datadog/*` import: it lives in the
 * `@indeed/jobsearch-react-native-bundle` package, which also builds for native,
 * so it must not pull in the web-only `@datadog/browser-logs` SDK. The one piece
 * that talks to the SDK — the `debug`/`info`/`warn`/`error` methods — receives
 * the logs `logger` by injection (`DatadogLogsSink`); each web surface passes
 * its own `datadogLogs.logger`, where `@datadog/browser-logs` is a declared dep.
 */

export type DatadogWebConfig = {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  version: string;
  env: string;
  sessionSampleRate: number;
  sessionReplaySampleRate?: number;
  trackResources?: boolean;
  trackLongTasks?: boolean;
  trackUserInteractions?: boolean;
  defaultPrivacyLevel?: "allow" | "mask" | "mask-user-input";
};

export type RequiredDatadogWebConfig = Pick<
  DatadogWebConfig,
  "applicationId" | "clientToken" | "site" | "service" | "version" | "env" | "sessionSampleRate"
>;

const TOKEN_SUFFIX_PATTERN = /token$/i;
const SENSITIVE_FIELD_NAMES = new Set([
  "email",
  "emailaddress",
  "phone",
  "phonenumber",
  "address",
  "password",
  "secret",
]);

export function hasRequiredConfig(
  config?: Partial<DatadogWebConfig>,
): config is RequiredDatadogWebConfig {
  return Boolean(
    config?.applicationId &&
    config.clientToken &&
    config.site &&
    config.service &&
    config.version &&
    config.env &&
    typeof config.sessionSampleRate === "number",
  );
}

export function sanitizeUrl(url?: string): string | undefined {
  if (!url) {
    return url;
  }

  try {
    const parsedUrl = new URL(url, globalThis.location?.origin);
    parsedUrl.search = "";
    parsedUrl.hash = "";
    return parsedUrl.toString();
  } catch {
    return String(url).split("?")[0]?.split("#")[0];
  }
}

function isSensitiveFieldName(fieldName: string): boolean {
  const normalizedFieldName = fieldName.replace(/[_-]/g, "").toLowerCase();
  return SENSITIVE_FIELD_NAMES.has(normalizedFieldName) || TOKEN_SUFFIX_PATTERN.test(fieldName);
}

export function scrubContext(value: unknown): unknown {
  if (!value || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(scrubContext);
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      isSensitiveFieldName(key) ? "[REDACTED]" : scrubContext(entry),
    ]),
  );
}

export function createError(
  message: string,
  errorKind?: string,
  errorMessage?: string,
  stacktrace?: string,
): Error {
  const error = new Error(errorMessage ?? message);

  if (errorKind) {
    error.name = errorKind;
  }

  if (stacktrace) {
    error.stack = stacktrace;
  }

  return error;
}

export function shouldAttachError(
  errorKind?: string,
  errorMessage?: string,
  stacktrace?: string,
): boolean {
  return Boolean(errorKind || errorMessage || stacktrace);
}

/**
 * Minimal structural view of `@datadog/browser-logs`'s `datadogLogs.logger`.
 * Declared locally (rather than importing the SDK type) so this module stays
 * `@datadog/*`-free; `datadogLogs.logger` is structurally assignable to it.
 */
export type DatadogLogsSink = {
  debug: (message: string, context?: object, error?: Error) => void;
  info: (message: string, context?: object, error?: Error) => void;
  warn: (message: string, context?: object, error?: Error) => void;
  error: (message: string, context?: object, error?: Error) => void;
};

/** The logs-only `IDatadogLogger` methods; RUM methods are supplied per-surface. */
type DatadogLogsMethods = Pick<IDatadogLogger, "debug" | "info" | "warn" | "error">;

/**
 * The `debug`/`info`/`warn`/`error` methods shared by both web loggers.
 * `logger` is the injected `datadogLogs.logger`; `toDatadogContext` applies each
 * surface's own context enrichment (profile imports `enrichContext` at module
 * scope; viewjob injects it via deps).
 */
export function createDatadogLogsMethods(
  logger: DatadogLogsSink,
  toDatadogContext: (context?: Record<string, unknown>) => object,
): DatadogLogsMethods {
  return {
    debug: (message: string, context?: Record<string, unknown>): Promise<void> => {
      logger.debug(message, toDatadogContext(context));
      return Promise.resolve();
    },

    info: (message: string, context?: Record<string, unknown>): Promise<void> => {
      logger.info(message, toDatadogContext(context));
      return Promise.resolve();
    },

    warn: (
      message: string,
      context?: Record<string, unknown>,
      errorKind?: string,
      errorMessage?: string,
      stacktrace?: string,
    ): Promise<void> => {
      const datadogContext = toDatadogContext(context);
      if (shouldAttachError(errorKind, errorMessage, stacktrace)) {
        logger.warn(
          message,
          datadogContext,
          createError(message, errorKind, errorMessage, stacktrace),
        );
      } else {
        logger.warn(message, datadogContext);
      }
      return Promise.resolve();
    },

    error: (
      message: string,
      context?: Record<string, unknown>,
      errorKind?: string,
      errorMessage?: string,
      stacktrace?: string,
    ): Promise<void> => {
      const datadogContext = toDatadogContext(context);
      if (shouldAttachError(errorKind, errorMessage, stacktrace)) {
        logger.error(
          message,
          datadogContext,
          createError(message, errorKind, errorMessage, stacktrace),
        );
      } else {
        logger.error(message, datadogContext);
      }
      return Promise.resolve();
    },
  };
}
